import { useState, useEffect, type FormEvent } from 'react';

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}
import { api } from '../../lib/api';
import { Mail, Webhook, Send, CheckCircle, XCircle, Bug, Server, Network, Database, Download, RotateCcw, Plus } from 'lucide-react';

interface PlatformSettings {
  platform_name: string;
  support_email: string;
  default_tier: string;
  base_domain: string;
  // SMTP
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_password: string;
  smtp_from: string;
  smtp_from_name: string;
  // Webhooks
  webhook_url: string;
  webhook_enabled: boolean;
  webhook_events: string[];
  // Sentry
  sentry_dsn_api: string;
  sentry_dsn_frontend: string;
  // Anthropic
  anthropic_api_key: string;
  anthropic_model: string;
  // Appwrite Console
  appwrite_console_email: string;
  appwrite_console_password: string;
  // Monitoring & Backups
  monitoring_interval_minutes: number;
  health_check_interval_minutes: number;
  backup_time_utc: string;
  // Appwrite Server SSH
  appwrite_ssh_host: string;
  appwrite_ssh_port: number;
  appwrite_ssh_user: string;
  appwrite_ssh_password: string;
  appwrite_ssh_private_key: string;
  appwrite_ssh_auth_method: string;
  appwrite_db_password: string;
  // NPM
  npm_url: string;
  npm_email: string;
  npm_password: string;
  npm_forward_scheme: string;
  npm_forward_host: string;
  npm_forward_port: number;
  npm_letsencrypt_email: string;
}

const ALL_EVENTS = [
  { value: 'site_offline', label: 'Site offline' },
  { value: 'storage_high', label: 'Opslag bijna vol' },
  { value: 'disk_high', label: 'Schijfruimte bijna vol' },
  { value: 'backup_failed', label: 'Backup mislukt' },
  { value: 'appwrite_unhealthy', label: 'Appwrite onbereikbaar' },
];

const DEFAULTS: PlatformSettings = {
  platform_name: '',
  support_email: '',
  default_tier: 'free',
  base_domain: '',
  smtp_host: '',
  smtp_port: 587,
  smtp_user: '',
  smtp_password: '',
  smtp_from: '',
  smtp_from_name: '',
  webhook_url: '',
  webhook_enabled: false,
  webhook_events: ['site_offline', 'backup_failed', 'appwrite_unhealthy'],
  sentry_dsn_api: '',
  sentry_dsn_frontend: '',
  anthropic_api_key: '',
  anthropic_model: 'claude-opus-4-8',
  appwrite_console_email: '',
  appwrite_console_password: '',
  monitoring_interval_minutes: 60,
  health_check_interval_minutes: 15,
  backup_time_utc: '03:00',
  appwrite_ssh_host: '',
  appwrite_ssh_port: 22,
  appwrite_ssh_user: 'admin',
  appwrite_ssh_password: '',
  appwrite_ssh_private_key: '',
  appwrite_ssh_auth_method: 'password',
  appwrite_db_password: '',
  npm_url: '',
  npm_email: '',
  npm_password: '',
  npm_forward_scheme: 'http',
  npm_forward_host: '',
  npm_forward_port: 3000,
  npm_letsencrypt_email: '',
};

export default function Settings() {
  const [values, setValues] = useState<PlatformSettings>(DEFAULTS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [smtpTestResult, setSmtpTestResult] = useState<'ok' | 'fail' | null>(null);
  const [webhookTestResult, setWebhookTestResult] = useState<'ok' | 'fail' | null>(null);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [testingAppwrite, setTestingAppwrite] = useState(false);
  const [appwriteTestResult, setAppwriteTestResult] = useState<'ok' | 'fail' | null>(null);
  const [testingNpm, setTestingNpm] = useState(false);
  const [npmTestResult, setNpmTestResult] = useState<'ok' | 'fail' | null>(null);
  const [testingSSH, setTestingSSH] = useState(false);
  const [sshTestResult, setSSHTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'platform' | 'appwrite' | 'email' | 'integraties'>('platform');
  // DB Backup state
  const [dbBackups, setDbBackups] = useState<Array<{ id: string; name: string; size: number; date: string }>>([]);
  const [dbBackupsLoaded, setDbBackupsLoaded] = useState(false);
  const [dbBackupLoading, setDbBackupLoading] = useState(false);
  const [dbRestoreConfirm, setDbRestoreConfirm] = useState<string | null>(null);
  const [dbRestoring, setDbRestoring] = useState(false);
  const [dbError, setDbError] = useState<string | null>(null);
  const [dbSuccess, setDbSuccess] = useState<string | null>(null);

  useEffect(() => {
    api.get<PlatformSettings>('/settings')
      .then(data => setValues({ ...DEFAULTS, ...data }))
      .catch(e => setError(e.message))
      .finally(() => setIsLoading(false));
  }, []);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value, type } = e.target;
    setValues(v => ({
      ...v,
      [name]: type === 'number' ? parseInt(value) || 0 : value,
    }));
  }

  function toggleEvent(event: string) {
    setValues(v => ({
      ...v,
      webhook_events: v.webhook_events.includes(event)
        ? v.webhook_events.filter(e => e !== event)
        : [...v.webhook_events, event],
    }));
  }

  const [syncResult, setSyncResult] = useState<{ updated: number; failed: string[] } | null>(null);

  async function handleSync() {
    setIsSaving(true);
    setError('');
    setSuccess('');
    setSyncResult(null);
    try {
      const result = await api.post<{ updated: number; failed: string[] }>('/tenants/sync-base-domain');
      setSyncResult(result);
      setSuccess(`Base domain gesynchroniseerd naar ${result.updated} tenant(s).`);
      if (result.failed.length > 0) {
        setError(`Mislukt voor: ${result.failed.join(', ')}`);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sync mislukt.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/settings', values);
      setSuccess('Instellingen opgeslagen.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt.');
    } finally {
      setIsSaving(false);
    }
  }

  async function testSmtp() {
    setTestingSmtp(true);
    setSmtpTestResult(null);
    try {
      await api.post('/settings/test-smtp', { recipient: values.support_email || values.smtp_from });
      setSmtpTestResult('ok');
    } catch {
      setSmtpTestResult('fail');
    } finally {
      setTestingSmtp(false);
    }
  }

  async function testWebhook() {
    setTestingWebhook(true);
    setWebhookTestResult(null);
    try {
      await api.post('/settings/test-webhook');
      setWebhookTestResult('ok');
    } catch {
      setWebhookTestResult('fail');
    } finally {
      setTestingWebhook(false);
    }
  }

  async function testAppwriteConsole() {
    setTestingAppwrite(true);
    setAppwriteTestResult(null);
    try {
      await api.post('/settings/test-appwrite-console');
      setAppwriteTestResult('ok');
    } catch {
      setAppwriteTestResult('fail');
    } finally {
      setTestingAppwrite(false);
    }
  }

  async function testNpm() {
    setTestingNpm(true);
    setNpmTestResult(null);
    try {
      await api.post('/settings/test-npm');
      setNpmTestResult('ok');
    } catch {
      setNpmTestResult('fail');
    } finally {
      setTestingNpm(false);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-2xl">
        <h1 className="text-2xl font-bold text-gray-900">Instellingen</h1>
        <div className="card p-6 space-y-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'platform' as const, label: 'Platform' },
    { id: 'appwrite' as const, label: 'Appwrite' },
    { id: 'email' as const, label: 'E-mail & Notificaties' },
    { id: 'integraties' as const, label: 'Integraties' },
  ];

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900">Instellingen</h1>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-0 -mb-px">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
      {success && <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{success}</div>}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Platform Settings */}
        {activeTab === 'platform' && (
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Platform</h2>
            <div className="space-y-4">
              <div>
                <label className="label">Platformnaam</label>
                <input name="platform_name" type="text" value={values.platform_name} onChange={handleChange} className="input" placeholder="Occiput" />
              </div>
              <div>
                <label className="label">Support e-mailadres</label>
                <input name="support_email" type="email" value={values.support_email} onChange={handleChange} className="input" placeholder="support@occiput.io" />
              </div>
              <div>
                <label className="label">Standaard tier</label>
                <select name="default_tier" value={values.default_tier} onChange={handleChange} className="input">
                  <option value="free">Free</option>
                  <option value="pro">Pro</option>
                  <option value="business">Business</option>
                </select>
              </div>
              <div>
                <label className="label">Base Domain</label>
                <input name="base_domain" type="text" value={values.base_domain} onChange={handleChange} className="input" placeholder="bijv. doorkoppen.nl" />
                <p className="text-xs text-gray-500 mt-1">
                  Subdomeinen voor nieuwe tenants: slug.{values.base_domain || '...'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Appwrite */}
        {activeTab === 'appwrite' && (
          <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Server className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Appwrite</h2>
          </div>

          {/* Console credentials */}
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3">Console Toegang</p>
          <p className="text-sm text-gray-500 mb-3">
            Voor automatische CORS registratie en tenant provisioning.
          </p>
          <div className="space-y-4 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Console e-mail</label>
                <input name="appwrite_console_email" type="email" value={values.appwrite_console_email} onChange={handleChange} className="input" placeholder="admin@example.com" autoComplete="off" />
              </div>
              <div>
                <label className="label">Console wachtwoord</label>
                <input name="appwrite_console_password" type="password" value={values.appwrite_console_password} onChange={handleChange} className="input" placeholder="••••••••" autoComplete="new-password" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button type="button" disabled={testingAppwrite} className="btn-secondary text-sm flex items-center gap-2" onClick={testAppwriteConsole}>
                <Send className="w-4 h-4" />
                {testingAppwrite ? 'Testen...' : 'Test console verbinding'}
              </button>
              {appwriteTestResult === 'ok' && <span className="flex items-center gap-1 text-sm text-green-600"><CheckCircle className="w-4 h-4" /> Gelukt</span>}
              {appwriteTestResult === 'fail' && <span className="flex items-center gap-1 text-sm text-red-600"><XCircle className="w-4 h-4" /> Mislukt</span>}
            </div>
          </div>

          <hr className="border-gray-100 mb-6" />

          {/* SSH + Database backup */}
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3">Server SSH & Database Backup</p>
          <p className="text-sm text-gray-500 mb-3">
            SSH-toegang tot de Appwrite VM voor MariaDB backup en restore. Backups worden versleuteld opgeslagen in de Appwrite backups bucket.
          </p>
          <div className="space-y-4 mb-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="label">SSH Host</label>
                <input name="appwrite_ssh_host" type="text" value={values.appwrite_ssh_host} onChange={handleChange} className="input" placeholder="192.168.20.61" />
              </div>
              <div>
                <label className="label">Poort</label>
                <input name="appwrite_ssh_port" type="number" value={values.appwrite_ssh_port} onChange={handleChange} className="input" placeholder="22" />
              </div>
            </div>
            <div>
              <label className="label">Gebruikersnaam</label>
              <input name="appwrite_ssh_user" type="text" value={values.appwrite_ssh_user} onChange={handleChange} className="input" placeholder="admin" />
            </div>
            <div>
              <label className="label">Auth methode</label>
              <select name="appwrite_ssh_auth_method" value={values.appwrite_ssh_auth_method} onChange={handleChange} className="input">
                <option value="password">Wachtwoord</option>
                <option value="key">SSH Private Key</option>
              </select>
            </div>
            {values.appwrite_ssh_auth_method === 'password' ? (
              <div>
                <label className="label">SSH Wachtwoord</label>
                <input name="appwrite_ssh_password" type="password" value={values.appwrite_ssh_password} onChange={handleChange} className="input" placeholder="••••••••" autoComplete="new-password" />
              </div>
            ) : (
              <div>
                <label className="label">Private Key</label>
                <textarea name="appwrite_ssh_private_key" value={values.appwrite_ssh_private_key} onChange={(e) => setValues(v => ({ ...v, appwrite_ssh_private_key: e.target.value }))} rows={3} className="input font-mono text-xs" placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" />
              </div>
            )}
            <div>
              <label className="label">MariaDB Root Wachtwoord</label>
              <input name="appwrite_db_password" type="password" value={values.appwrite_db_password} onChange={handleChange} className="input" placeholder="••••••••" autoComplete="new-password" />
              <p className="text-xs text-gray-500 mt-1">Het wachtwoord van de MariaDB container (_APP_DB_PASS in Appwrite .env)</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                disabled={testingSSH || !values.appwrite_ssh_host}
                className="btn-secondary text-sm flex items-center gap-2"
                onClick={async () => {
                  setTestingSSH(true);
                  setSSHTestResult(null);
                  try {
                    await api.put('/settings', values);
                    const result = await api.post<{ ok: boolean; message: string }>('/db-backup/test');
                    setSSHTestResult(result);
                  } catch (e: any) {
                    setSSHTestResult({ ok: false, message: e.message });
                  } finally {
                    setTestingSSH(false);
                  }
                }}
              >
                <Send className="w-4 h-4" />
                {testingSSH ? 'Testen...' : 'Test SSH verbinding'}
              </button>
              {sshTestResult?.ok && <span className="flex items-center gap-1 text-sm text-green-600"><CheckCircle className="w-4 h-4" /> {sshTestResult.message}</span>}
              {sshTestResult && !sshTestResult.ok && <span className="flex items-center gap-1 text-sm text-red-600"><XCircle className="w-4 h-4" /> {sshTestResult.message}</span>}
            </div>
          </div>

          <hr className="border-gray-100 mb-6" />

          {/* Monitoring intervals */}
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3">Monitoring & Backup Planning</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Site check interval (minuten)</label>
                <input name="monitoring_interval_minutes" type="number" min="5" max="1440" value={values.monitoring_interval_minutes} onChange={handleChange} className="input" />
                <p className="text-xs text-gray-500 mt-1">Hoe vaak site beschikbaarheid en opslag wordt gecontroleerd</p>
              </div>
              <div>
                <label className="label">Health check interval (minuten)</label>
                <input name="health_check_interval_minutes" type="number" min="5" max="1440" value={values.health_check_interval_minutes} onChange={handleChange} className="input" />
                <p className="text-xs text-gray-500 mt-1">Hoe vaak Appwrite systeemstatus wordt gecontroleerd</p>
              </div>
            </div>
            <div>
              <label className="label">Tenant backup tijdstip (UTC)</label>
              <input name="backup_time_utc" type="time" value={values.backup_time_utc} onChange={handleChange} className="input w-40" />
              <p className="text-xs text-gray-500 mt-1">Dagelijkse automatische tenant backup. UTC+2 = {(() => { const [h, m] = values.backup_time_utc.split(':').map(Number); return `${String((h + 2) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`; })()} CET</p>
            </div>
          </div>

          {values.appwrite_ssh_host && (
            <>
              <hr className="border-gray-100 my-6" />

              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3">Database Backups</p>
              {dbError && <div className="p-2 mb-3 bg-red-50 border border-red-200 rounded text-red-700 text-xs">{dbError}</div>}
              {dbSuccess && <div className="p-2 mb-3 bg-green-50 border border-green-200 rounded text-green-700 text-xs">{dbSuccess}</div>}

              <div className="flex items-center gap-2 mb-3">
                <button
                  type="button"
                  onClick={async () => {
                    setDbBackupLoading(true); setDbError(null); setDbSuccess(null);
                    try {
                      const result = await api.post<{ ok: boolean; name: string; size: number }>('/db-backup');
                      setDbSuccess(`Backup aangemaakt: ${result.name} (${formatBytes(result.size)})`);
                      const list = await api.get<{ backups: typeof dbBackups }>('/db-backup');
                      setDbBackups(list.backups);
                      setDbBackupsLoaded(true);
                    } catch (e: any) { setDbError(e.message); }
                    finally { setDbBackupLoading(false); }
                  }}
                  disabled={dbBackupLoading}
                  className="btn-primary text-sm flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {dbBackupLoading ? 'Bezig...' : 'Backup maken'}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const list = await api.get<{ backups: typeof dbBackups }>('/db-backup');
                      setDbBackups(list.backups);
                      setDbBackupsLoaded(true);
                    } catch (e: any) { setDbError(e.message); }
                  }}
                  className="btn-secondary text-sm"
                >
                  {dbBackupsLoaded ? 'Vernieuwen' : 'Backups laden'}
                </button>
              </div>

              {dbBackupsLoaded && (
                dbBackups.length === 0
                  ? <p className="text-sm text-gray-400">Geen database backups.</p>
                  : <div className="space-y-1">
                      {dbBackups.map(b => (
                        <div key={b.id} className="flex items-center justify-between text-sm py-2 px-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Database className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-gray-700">{b.name}</span>
                            <span className="text-xs text-gray-400">{formatBytes(b.size)}</span>
                            <span className="text-xs text-gray-400">{new Date(b.date).toLocaleString('nl-NL')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={`/api/db-backup/download/${b.id}?token=${localStorage.getItem('occiput_token')}`}
                              className="text-xs text-brand-600 hover:underline flex items-center gap-1"
                            >
                              <Download className="w-3 h-3" /> Download
                            </a>
                            {dbRestoreConfirm === b.id ? (
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-red-600">Alle data wordt overschreven!</span>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    setDbRestoring(true); setDbError(null);
                                    try {
                                      const result = await api.post<{ ok: boolean; message: string }>(`/db-backup/restore/${b.id}`);
                                      setDbSuccess(result.message);
                                    } catch (e: any) { setDbError(e.message); }
                                    finally { setDbRestoring(false); setDbRestoreConfirm(null); }
                                  }}
                                  disabled={dbRestoring}
                                  className="text-xs font-medium text-white bg-red-600 hover:bg-red-700 px-2 py-0.5 rounded"
                                >
                                  {dbRestoring ? 'Herstellen...' : 'Ja, herstel'}
                                </button>
                                <button type="button" onClick={() => setDbRestoreConfirm(null)} className="text-xs text-gray-500">Annuleren</button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setDbRestoreConfirm(b.id)}
                                className="text-xs text-amber-600 hover:underline flex items-center gap-1"
                              >
                                <RotateCcw className="w-3 h-3" /> Restore
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await api.delete(`/db-backup/${b.id}`);
                                  setDbBackups(prev => prev.filter(x => x.id !== b.id));
                                } catch (e: any) { setDbError(e.message); }
                              }}
                              className="text-xs text-red-500 hover:underline"
                            >
                              Verwijderen
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
              )}
            </>
          )}
          </div>
        )}

        {/* Integraties tab */}
        {activeTab === 'integraties' && (
          <>
          <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Network className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Nginx Proxy Manager</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Voor automatische proxy host en SSL configuratie bij domein-wijzigingen. Laat leeg om handmatig in NPM te configureren.
          </p>
          <div className="space-y-4">
            <div>
              <label className="label">NPM URL</label>
              <input name="npm_url" type="text" value={values.npm_url} onChange={handleChange} className="input" placeholder="http://192.168.20.75:81" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">NPM e-mail</label>
                <input name="npm_email" type="email" value={values.npm_email} onChange={handleChange} className="input" placeholder="admin@example.com" autoComplete="off" />
              </div>
              <div>
                <label className="label">NPM wachtwoord</label>
                <input name="npm_password" type="password" value={values.npm_password} onChange={handleChange} className="input" placeholder="••••••••" autoComplete="new-password" />
              </div>
            </div>
            <hr className="border-gray-100" />
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Forward configuratie</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">Scheme</label>
                <select name="npm_forward_scheme" value={values.npm_forward_scheme} onChange={handleChange} className="input">
                  <option value="http">http</option>
                  <option value="https">https</option>
                </select>
              </div>
              <div>
                <label className="label">Host</label>
                <input name="npm_forward_host" type="text" value={values.npm_forward_host} onChange={(e) => { e.target.value = e.target.value.replace(/^https?:\/\//, ''); handleChange(e); }} className="input" placeholder="192.168.20.96" />
                <p className="text-xs text-gray-400 mt-1">Alleen IP of hostname, zonder http://</p>
              </div>
              <div>
                <label className="label">Poort</label>
                <input name="npm_forward_port" type="number" value={values.npm_forward_port} onChange={handleChange} className="input" placeholder="3000" />
              </div>
            </div>
            <div>
              <label className="label">Let's Encrypt e-mail</label>
              <input name="npm_letsencrypt_email" type="email" value={values.npm_letsencrypt_email} onChange={handleChange} className="input" placeholder="ssl@example.com" />
              <p className="text-xs text-gray-500 mt-1">Wordt gebruikt als er geen bestaand (wildcard) certificaat gevonden wordt.</p>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button type="button" disabled={testingNpm || !values.npm_url} className="btn-secondary text-sm flex items-center gap-2" onClick={testNpm}>
                <Send className="w-4 h-4" />
                {testingNpm ? 'Testen...' : 'Test verbinding'}
              </button>
              {npmTestResult === 'ok' && <span className="flex items-center gap-1 text-sm text-green-600"><CheckCircle className="w-4 h-4" /> Gelukt</span>}
              {npmTestResult === 'fail' && <span className="flex items-center gap-1 text-sm text-red-600"><XCircle className="w-4 h-4" /> Mislukt</span>}
            </div>
          </div>
        </div>

        {/* Sentry Error Tracking */}
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Bug className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Error Tracking (Sentry)</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Configureer Sentry DSN's om fouten automatisch te tracken. Maak een project aan op <a href="https://sentry.io" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">sentry.io</a> en kopieer de DSN.
          </p>
          <div className="space-y-4">
            <div>
              <label className="label">Sentry DSN (API / Server)</label>
              <input name="sentry_dsn_api" type="text" value={values.sentry_dsn_api} onChange={handleChange} className="input font-mono text-sm" placeholder="https://abc123@o123.ingest.sentry.io/456" />
              <p className="text-xs text-gray-500 mt-1">Voor server-side error tracking (API fouten, crashes)</p>
            </div>
            <div>
              <label className="label">Sentry DSN (Frontend / Browser)</label>
              <input name="sentry_dsn_frontend" type="text" value={values.sentry_dsn_frontend} onChange={handleChange} className="input font-mono text-sm" placeholder="https://def456@o123.ingest.sentry.io/789" />
              <p className="text-xs text-gray-500 mt-1">Voor client-side error tracking (JavaScript fouten in de browser)</p>
            </div>
            {(values.sentry_dsn_api || values.sentry_dsn_frontend) && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                Sentry DSN wijzigingen worden pas actief na een herstart/redeploy van de betreffende service.
              </div>
            )}
          </div>
        </div>

        {/* Anthropic */}
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Anthropic (achtergrond-generator)</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Gebruikt door de Achtergronden-generator om effecten te genereren.
          </p>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3">
                API Toegang
              </p>
              <label className="label">API-sleutel</label>
              <input
                name="anthropic_api_key"
                type="password"
                value={values.anthropic_api_key}
                onChange={handleChange}
                className="input"
                placeholder="sk-ant-..."
                autoComplete="new-password"
              />
              <p className="text-xs text-gray-500 mt-1">
                Versleuteld opgeslagen. De waarde wordt gemaskeerd weergegeven na opslaan.
              </p>
            </div>
            <div>
              <label className="label">Model</label>
              <input
                name="anthropic_model"
                type="text"
                value={values.anthropic_model}
                onChange={handleChange}
                className="input"
                placeholder="claude-opus-4-8"
              />
              <p className="text-xs text-gray-500 mt-1">
                Het Claude-model dat gebruikt wordt voor het genereren van achtergrondeffecten.
              </p>
            </div>
          </div>
        </div>
          </>
        )}

        {/* E-mail & Notificaties tab */}
        {activeTab === 'email' && (
          <>
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">E-mail (SMTP)</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">Configureer SMTP om emails te versturen (welkomstberichten, notificaties, wachtwoord resets).</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">SMTP Host</label>
                <input name="smtp_host" type="text" value={values.smtp_host} onChange={handleChange} className="input" placeholder="smtp.example.com" />
              </div>
              <div>
                <label className="label">SMTP Port</label>
                <input name="smtp_port" type="number" value={values.smtp_port} onChange={handleChange} className="input" placeholder="587" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Gebruikersnaam</label>
                <input name="smtp_user" type="text" value={values.smtp_user} onChange={handleChange} className="input" placeholder="user@example.com" autoComplete="off" />
              </div>
              <div>
                <label className="label">Wachtwoord</label>
                <input name="smtp_password" type="password" value={values.smtp_password} onChange={handleChange} className="input" placeholder="••••••••" autoComplete="new-password" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Afzender e-mail</label>
                <input name="smtp_from" type="email" value={values.smtp_from} onChange={handleChange} className="input" placeholder="noreply@example.com" />
              </div>
              <div>
                <label className="label">Afzender naam</label>
                <input name="smtp_from_name" type="text" value={values.smtp_from_name} onChange={handleChange} className="input" placeholder="Occiput Platform" />
              </div>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                disabled={testingSmtp || !values.smtp_host}
                className="btn-secondary text-sm flex items-center gap-2"
                onClick={testSmtp}
              >
                <Send className="w-4 h-4" />
                {testingSmtp ? 'Testen...' : 'Test e-mail versturen'}
              </button>
              {smtpTestResult === 'ok' && <span className="flex items-center gap-1 text-sm text-green-600"><CheckCircle className="w-4 h-4" /> Gelukt</span>}
              {smtpTestResult === 'fail' && <span className="flex items-center gap-1 text-sm text-red-600"><XCircle className="w-4 h-4" /> Mislukt</span>}
            </div>
          </div>
        </div>

        {/* Webhook Settings */}
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Webhook className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Webhooks</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">Ontvang notificaties over platform events via een webhook URL (bijv. Slack, Discord, ntfy).</p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={values.webhook_enabled}
                  onChange={e => setValues(v => ({ ...v, webhook_enabled: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-600" />
              </label>
              <span className="text-sm text-gray-700">Webhooks actief</span>
            </div>
            <div>
              <label className="label">Webhook URL</label>
              <input
                name="webhook_url"
                type="url"
                value={values.webhook_url}
                onChange={handleChange}
                className="input"
                placeholder="https://hooks.slack.com/services/... of https://ntfy.sh/topic"
              />
            </div>
            <div>
              <label className="label mb-2 block">Events</label>
              <div className="space-y-2">
                {ALL_EVENTS.map(evt => (
                  <label key={evt.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={values.webhook_events.includes(evt.value)}
                      onChange={() => toggleEvent(evt.value)}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm text-gray-700">{evt.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                disabled={testingWebhook || !values.webhook_url}
                className="btn-secondary text-sm flex items-center gap-2"
                onClick={testWebhook}
              >
                <Send className="w-4 h-4" />
                {testingWebhook ? 'Testen...' : 'Test webhook versturen'}
              </button>
              {webhookTestResult === 'ok' && <span className="flex items-center gap-1 text-sm text-green-600"><CheckCircle className="w-4 h-4" /> Gelukt</span>}
              {webhookTestResult === 'fail' && <span className="flex items-center gap-1 text-sm text-red-600"><XCircle className="w-4 h-4" /> Mislukt</span>}
            </div>
          </div>
        </div>
          </>
        )}

        {/* Save */}
        <div className="flex gap-3">
          <button type="submit" disabled={isSaving} className="btn-primary">
            {isSaving ? 'Opslaan...' : 'Alle instellingen opslaan'}
          </button>
          <button
            type="button"
            disabled={isSaving || !values.base_domain}
            className="btn-secondary"
            onClick={handleSync}
          >
            Sync base domain naar tenants
          </button>
        </div>
      </form>
    </div>
  );
}
